"use client";

import { Button, Loader } from "@habacuscode/habacus_ui_component_library";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { postData } from "@/utils/client/client_utils";

export default function WelcomeStep({
    locale,
    verificationUuid,
}: Readonly<{ locale: string; verificationUuid?: string | null }>) {
    const t = useTranslations("Welcome");
    const router = useRouter();
    const [name, setName] = useState<string | undefined>();
    const [ready, setReady] = useState(false);

    useEffect(() => {
        if (!verificationUuid) {
            router.replace(`/${locale}/error?reason=invalid`);
            return;
        }

        const fetchVerification = async () => {
            try {
                const res = await postData(
                    "/api/verification",
                    { verificationUuid },
                    false,
                );
                const data = await res.json().catch(() => null);

                if (!res.ok) {
                    router.replace(`/${locale}/error?reason=invalid`);
                    return;
                }

                const validTo = data?.result?.validTo;
                if (validTo && new Date(validTo).getTime() < Date.now()) {
                    router.replace(`/${locale}/error?reason=link`);
                    return;
                }

                setName(data?.result?.name);
                setReady(true);
            } catch {
                router.replace(`/${locale}/error?reason=invalid`);
            }
        };

        fetchVerification();
    }, [verificationUuid, locale, router]);

    const handleContinue = () => {
        router.push(`/${locale}/login`);
    };

    const container =
        "min-h-screen flex flex-col justify-center px-8 lg:px-16 max-w-2xl mx-auto";

    if (!ready) {
        return (
            <div className={container}>
                <Loader visible text="" />
            </div>
        );
    }

    return (
        <div className={container}>
            <h1 className="text-blue-950 text-3xl font-bold mb-4">
                {t("title", { name: name ?? "" })}
            </h1>
            <p className="text-blue-950 text-base mb-8">{t("description")}</p>
            <div>
                <Button
                    name="welcome_continue"
                    variant="primary"
                    size="md"
                    onClick={handleContinue}
                >
                    {t("continue")}
                </Button>
            </div>
        </div>
    );
}
